import { Link } from 'react-router-dom';

import { TConfig, TGetAddOns } from '@@/interfaces';
import { excludeFalse } from '@@/utils/excludeFalse';

import { AUTO_DISCOUNTS } from '../../autoDiscounts';

const defaultVehicleSideEffectMessage =
  'This coverage is only available if Comprehensive & Collision is also selected.';

const ToggleConfig: DeepPartial<TConfig> = {
  auto: {
    addOns: {
      options: {
        towing_road_services: {
          copy: {
            label: 'Towing & Road Service(s)',
            description:
              "Getting stranded with a flat is no fun. Especially if you don't know a jack from a lug wrench. The coverage might be optional but pot holes in winter are not.",
            modalContent:
              'Towing & Road Service(s) is an optional coverage and pays for the cost of towing and other roadside services like a locksmith, flat tire changing, and battery jumpstart in the event your vehicle is disabled (subject to the Service Call conditions).',
          },
        },
        ridesharing: {
          copy: {
            label: 'Ridesharing',
            description:
              "If you're earning extra dough through rideshare or food delivery, this coverage is designed to complement other insurance that may be provided. Our lives can be pretty blurred these days - one minute you're driving to your friend's house and the next you're dropping off a food order, so add additional protection.",
            modalContent: [
              'Ridesharing is an optional coverage and is applicable if you earn a side income (part-time employment, earning up to $25,000 annually) doing rideshare or on-demand food delivery.',
              "If you're a rideshare driver, it extends the coverage shown on your Declarations Page from the time when you're available to receive requests to the time you're engaged in a prearranged ride. Once the prearranged ride has begun, it extends the coverage shown on your Declarations Page except Liability.",
              "If your side hustle is on-demand food delivery, it extends the coverage shown on your Declarations Page from the time you're available to receive a food delivery request to the time you're engaged in a prearranged delivery. Once the prearranged delivery has begun, it extends the coverage shown on your Declarations Page with two exceptions - Liability coverage up to the minimum limits mandated by your state and Comprehensive & Collision coverage up to $1,000 or the deductible you selected, whichever is higher.",
            ],
          },
        },
        loan_lease_payoff: {
          copy: {
            label: 'Residual Debt (loan payoff)',
            description:
              "Accidents don't wait for your car to be paid off. If your car is totaled, this coverage can help with any remaining loan or lease amount you owe.",
            modalContent:
              'This coverage is optional and can help pay for the additional amount you owe on a vehicle loan or lease beyond the insurance coverage for a vehicle that is totaled.',
          },
        },
        new_car_replacement: {
          copy: {
            label: 'New Car Replacement',
            description:
              "Nothing stinks more than your beautiful new car getting totaled except maybe getting only the depreciated value of it back. This coverage can help get you a brand new replacement so it's like it never happened.",
            modalContent:
              'New Car Replacement is an optional coverage that pays for the cost of replacing your vehicle with a brand new one of the same make and model rather than the depreciated value of yours, if it is totaled and less than two years old.',
          },
        },
        loss_of_use: {
          copy: {
            label: 'Loss of Use',
            description:
              "While you're without your car after an accident, this coverage helps provide the alternate transportation you need. It's optional but if something happens to your car, ask yourself if you really want to count on your buddy getting you to work.",
            modalContent:
              'Loss of Use is optional and provides a flat amount per day up to the policy limit for alternative transportation such as ridesharing, car rental, taxicab, or public transit while your vehicle is being repaired or replaced following a covered loss.',
          },
        },
        temporary_ride: {
          copy: {
            label: 'Temporary Ride Coverage',
            description:
              "While you're without your car after an accident, this coverage helps provide the alternate transportation you need. It's optional but if something happens to your car, ask yourself if you really want to count on your buddy getting you to work.",
            modalContent:
              'Temporary Ride Coverage is optional and provides a flat amount per day up to the policy limit for alternative transportation such as ridesharing, car rental, taxicab, or public transit while your vehicle is being repaired or replaced following a covered loss.',
          },
        },
        side_hustle: {
          copy: {
            label: 'Side Hustle',
            description:
              "If you're earning extra dough through rideshare or food delivery, this coverage is designed to complement other insurance that may be provided. Our lives can be pretty blurred these days - one minute you're driving to your friend's house and the next you're dropping off a food order, so add additional protection.",
            modalContent: [
              'Side Hustle Auto is an optional coverage and is applicable if you have part-time employment, earning up to $25,000 annually doing rideshare or on-demand food delivery.',
              `If you're a rideshare driver, it extends the coverage shown on your Declarations Page from the time when you're available to receive requests to the time you're engaged in a prearranged ride. Once the prearranged ride has begun, it extends the coverage shown on your Declarations Page except Liability.`,
              `If your side hustle is on-demand food delivery, it extends the coverage shown on your Declarations Page from the time you're available to receive a food delivery request to the time you're engaged in a prearranged delivery. Once the prearranged delivery has begun, it extends the coverage shown on your Declarations Page with two exceptions - Liability coverage up to the minimum limits mandated by your state and Comprehensive & Collision coverage up to $1,000 or the deductible you selected, whichever is higher.`,
              'In the event of a covered loss to your vehicle, Side Hustle Auto also covers property damage to your business personal property up to $1,000 as well as loss of business income ($500 per week for a maximum of 4 weeks) if you have purchased Comprehensive & Collision coverage. One claim allowed per policy period.',
            ],
          },
        },
        gap: {
          copy: {
            label: 'Gap Coverage',
            description:
              "Accidents don't wait for your car to be paid off. If your car is totaled, this coverage can help with any remaining loan or lease amount you owe.",
            modalContent:
              'This coverage is optional and can help pay for the additional amount you owe on a vehicle loan or lease beyond the insurance coverage for a vehicle that is totaled.',
          },
        },
        pet_passenger: {
          copy: {
            label: 'Pet Passenger℠',
            description:
              "No one loves a good car ride more than Fido, but if you're taking him along you might want to add this coverage. It's optional but Fido's big puppy dog eyes are telling you to think twice about going without.",
            modalContent:
              'Pet Passenger is an optional coverage that pays for the cost of veterinary care, burial, disposal, injury, or death of household pets resulting from a covered loss, up to a aggregate maximum limit of $1,000.',
          },
        },
        glass: {
          copy: {
            label: 'Glass & Safety',
            description:
              'Would you like to have a $0 deductible on glass only claims?',
            modalContent:
              "For an additional premium, you can choose a $0 deductible for glass only claims. This includes windshields and windows as well as damage to plastic or other material used in your vehicle's lights.",
          },
        },
        oem_parts: {
          copy: {
            label: 'Original Parts Replacement',
            description:
              "If you're passionate about your car (i.e., it has a name) and want only factory part replacements in the event of an accident, then this is the coverage for you (and Princess Priscilla von Prius).",
            modalContent:
              'This is an optional coverage that pays for original factory equipment replacement parts in the event that your vehicle is damaged in a covered loss.',
          },
        },
      },
      getAddOns: ({ userAction }: Parameters<TGetAddOns>[0]) =>
        excludeFalse([
          'towing_road_services',
          'temporary_ride',
          'side_hustle',
          'gap',
          'oem_parts',
          'new_car_replacement',
          userAction === 'editing-addons' && 'pet_passenger',
        ] as const),
    },
  },
  autoDiscounts: [
    AUTO_DISCOUNTS.antiTheft,
    AUTO_DISCOUNTS.aeb,
    AUTO_DISCOUNTS.continuousInsurance,
    AUTO_DISCOUNTS.defensiveDriver,
    AUTO_DISCOUNTS.distantStudent,
    AUTO_DISCOUNTS.goodStudent,
    AUTO_DISCOUNTS.homeowner,
    AUTO_DISCOUNTS.multiCar,
    AUTO_DISCOUNTS.multiPolicy,
    AUTO_DISCOUNTS.accidentFree,
    AUTO_DISCOUNTS.safeDriving,
    AUTO_DISCOUNTS.teenDriver,
    AUTO_DISCOUNTS.threeYearSafeDriving,
  ],
  coverage: {
    propertyDamage: {
      title: 'Property Damage Liability',
      description:
        "You don't really get to pick which car you accidentally rear-end - whether it's a beater or a bimmer, this coverage can help.",
      modalContent:
        "Property damage (PD) coverage pays for damages if you or the driver of your vehicle are found legally obligated to pay for accidental property damage to someone else's property due to a covered loss, up to the limit of coverage chosen by you. This is a mandatory coverage.",
    },
    medical: {
      title: 'Medical',
      description:
        'Accidents can be scary, especially when you or someone you love is injured. If that happens, though, this coverage can help lessen the impact to your wallet (or theirs).',
      modalContent:
        'Medical coverage pays reasonable medical and funeral expenses incurred by an insured person on your policy caused by an accident, up to the limit selected and regardless of fault.',
      textAlertContent: '',
    },
    underinsuredMotoristPropertyDamage: {
      description:
        'You can hope that all other drivers have enough insurance to pay for damage they cause to your car. Or you can protect yourself with this coverage, which helps cover the difference.',
      modalContent:
        'Underinsured Motorist Property Damage (UIMPD) pays any gap between the insurance available to the at-fault driver and the limits chosen, for amounts that you are legally entitled to recover for physical damage to your insured car, sustained in an accident caused by a driver who has insufficient insurance.',
      title: 'Underinsured Motorist Property Damage',
    },
    uninsuredMotoristPropertyDamage: {
      description: `Your bumper sticker might say "don't hit me if you're uninsured" but it isn't actually protecting you against that. Don't worry, this coverage can.`,
      modalContent:
        'Uninsured Motorist Property Damage (UMPD) pays, up to the limits chosen, the amount you are legally entitled to recover for physical damages to your insured car sustained in an accident caused by a driver who is not insured.',
      title: 'Uninsured Motorist Property Damage',
    },
    underinsuredMotoristBodilyInjury: {
      title: 'Underinsured Motorist Bodily Injury',
      description:
        'If you or your passenger is injured in an accident and the driver at fault has insurance but not quite enough, this coverage can help make up the difference.',
      modalContent:
        'Underinsured Motorist Bodily Injury (UIMBI) pays for damages that an insured person on your policy is legally entitled to recover for bodily injury or death caused by an at-fault driver with insufficient insurance.',
      textAlertContent:
        'We’ve decreased your Underinsured motorist bodily injury and Underinsured motorist bodily injury limits to match the changes you’ve made to your Bodily injury coverage. ',
    },
    uninsuredMotoristBodilyInjury: {
      title: 'Uninsured Motorist Bodily Injury',
      description:
        "That pain in your neck is not just from the accident - it's also from finding out that the person who hit you has no insurance. This coverage can help.",
      modalContent:
        'Uninsured Motorist Bodily Injury (UMBI) pays the amount you are legally entitled to recover for damages from bodily injury, up to the limit of liability, when an insured person on your policy is hit by an uninsured driver or an unidentified (e.g. hit-and-run) driver.',
      textAlertContent:
        'We’ve decreased your Uninsured motorist bodily injury and Underinsured motorist bodily injury limits to match the changes you’ve made to your Bodily injury coverage.',
    },
  },
  initialCoverageValues: {
    collision_deductible: '0',
    new_car_replacement: 'false',
    oem_parts: 'false',
    towing_road_services: 'false',
  },
  matureDriver: {
    getSubjectText: ({
      isPni,
      subjectText,
    }: Parameters<NonNullable<TConfig['matureDriver']['getSubjectText']>>[0]) =>
      `${isPni ? 'Have you' : `Has ${subjectText}`}`,
    label:
      ' completed a mature driver motor vehicle accident prevention course?',
    explanation:
      'Approved defensive driving courses designed for mature drivers help improve driving skills and reduce driving risks by anticipating situations and making safe well-informed decisions. Courses required because of a driving offense or traffic violation do not apply.',
  },
  visibleCoverages: {
    overrides: [],
    protectionCoverages: [
      { label: 'Medical', key: 'medical_limit' },
      {
        label: 'Uninsured Motorist Bodily Injury',
        key: 'uninsured_motorist_bodily_injury_limit',
      },
    ],
    protectYourAssetsCoverages: [
      { label: 'Bodily Injury', key: 'bodily_injury_limit' },
      { label: 'Property Damage Liability', key: 'property_damage_limit' },
    ],
    vehicleCoverages: [
      {
        label: 'Comprehensive & Collision',
        key: 'collision_deductible',
        overrideValueLabel: 'Deductible',
      },
    ],
  },
  formDisclaimer: (
    <>
      To prepare your personalized quote, we use information from you and 3rd
      party sources (like your credit, claims and/or driving histories).
      Don&apos;t worry, getting your price will not affect your credit score. To
      learn more about our information practices, including how to access and
      correct your information, please review our{' '}
      <Link className="link" to="/privacy">
        Privacy Policy
      </Link>{' '}
      and{' '}
      <Link className="link" to="/privacy">
        Notice of Information Practices
      </Link>
      .
    </>
  ),
  supportEmail: 'support@gettoggle.com',
  vehicleSideEffectMessages: {
    towing_road_services: defaultVehicleSideEffectMessage,
    loss_of_use: defaultVehicleSideEffectMessage,
    loan_lease_payoff:
      'This coverage is only available if Comprehensive & Collision is also selected, the vehicle is financed or leased, and the model year is three (3) years old or less.',
    oem_parts:
      'This coverage is only available if Comprehensive & Collision is also selected and the model year is ten (10) years old or less.',
    new_car_replacement:
      'This coverage is only available if Comprehensive & Collision is also selected and the model year is one (1) year old or less.',
    temporary_ride: defaultVehicleSideEffectMessage,
    side_hustle: defaultVehicleSideEffectMessage,
    gap:
      'This coverage is only available if Comprehensive & Collision is also selected, the vehicle is financed or leased, and the model year is three (3) years old or less.',
    glass: defaultVehicleSideEffectMessage,
    uninsured_motorist_property_damage_limit: defaultVehicleSideEffectMessage,
  },
};
export default ToggleConfig;
